import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'src/components/plotly';
import PlotProps from 'src/components/plotly/types';
import { useRefDimensions } from 'src/hooks/useRefDefinitions';
import { modeBarButtonsToRemove } from 'src/utils/constants';
import { getHoursMinutesAndSeconds } from 'src/utils/helpers';
import Loader from '../Loader';

import styles from './styles.module.css';

export type PlotHeatmapProps = {
  data: any[];
  imageSrc?: string;
  type?: string;
  maxValue?: number;
  loading?: boolean;
  averageTimeData?: number[][];
};

const HEATMAP_GRID_SIZE = 51;
const DEFAULT_IMAGE_ASPECT_RATIO = 16 / 9;

const heatmapColorScale: Array<[number, string]> = [
  [0, 'rgba(255,255,255,0)'],
  [0.000001, '#92ff57'],
  [0.2, '#59ff00'],
  [0.4, '#fafa00'],
  [0.6, '#ffbf00'],
  [0.8, '#ff5c5c'],
  [1, '#ff0000'],
];

export const PlotHeatmap = ({
  data,
  imageSrc = 'https://i.ytimg.com/vi/hyhR_NoJyr8/maxresdefault.jpg',
  maxValue = 1,
  type,
  loading,
  averageTimeData = [],
}: PlotHeatmapProps) => {
  const [imageAspectRatio, setImageAspectRatio] = useState(DEFAULT_IMAGE_ASPECT_RATIO);
  const { width, ref } = useRefDimensions({});
  const plotHeight = Math.round((width || 750) / imageAspectRatio);
  const dataMax = useMemo(
    () => data.reduce(
      (maximum, row) => Math.max(maximum, ...row.map((value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : 0))),
      0,
    ),
    [data],
  );
  const zMax = Math.max(type === 'footfall' ? Math.max(maxValue, dataMax) : dataMax, 1);

  useEffect(() => {
    if (!imageSrc) return undefined;

    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth && image.naturalHeight) {
        setImageAspectRatio(image.naturalWidth / image.naturalHeight);
      }
    };
    image.src = imageSrc;

    return () => {
      image.onload = null;
    };
  }, [imageSrc]);

  const layout: any = {
    margin: {
      t: 0,
      r: 0,
      b: 0,
      l: 0,
    },
    showlegend: false,
    autosize: false,
    height: plotHeight,
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',

    images: imageSrc
      ? [
        {
          source: imageSrc,
          xref: 'x',
          yref: 'y',
          x: -0.5,
          y: HEATMAP_GRID_SIZE - 0.5,
          sizex: HEATMAP_GRID_SIZE,
          sizey: HEATMAP_GRID_SIZE,
          xanchor: 'left',
          yanchor: 'top',
          sizing: 'stretch',
          opacity: 0.7,
          layer: 'below',
        },
      ]
      : [],

    xaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
      fixedrange: true,
      range: [-0.5, HEATMAP_GRID_SIZE - 0.5],
    },
    yaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
      fixedrange: true,
      range: [-0.5, HEATMAP_GRID_SIZE - 0.5],
    },
  };

  const config: Partial<PlotProps['config']> = {
    // displayModeBar: false,
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove,
  };

  if (loading) {
    return (
      <div className="h-[450px] pt-[25%] pr-[12%]">
        <Loader />
      </div>
    );
  }
  const formatHoverTemplate = (type?: string) => {
    if (!type || type === 'footfall') {
      return 'Total Footfall: %{z}<br>Average Time: %{customdata}<extra></extra>';
    }
    if (type === 'totaltime') {
      return 'Total Time: %{customdata}<extra></extra>';
    }
    if (type === 'avgtime') {
      return 'Average Time: %{customdata}<extra></extra>';
    }
    return 'Total Footfall: %{z}<extra></extra>';
  };

  const valuesForTooltip = type === 'footfall' ? averageTimeData : data;
  const customData = valuesForTooltip.map((row) => row.map((value: number) => getHoursMinutesAndSeconds(value)));

  return (
    <div ref={ref} className={styles.heatmapContainer}>
      <Plot
        data={[
          {
            z: data,
            customdata: customData,
            xgap: 0.1,
            ygap: 0.1,
            zmin: 0,
            zmax: zMax,
            zauto: false,
            type: 'heatmap',
            mode: 'markers',
            colorscale: heatmapColorScale,
            showscale: false,
            opacity: 0.55,
            // TEMPORARY: Hide tooltips until accuracy issues are corrected.
            // Restore hovertemplate and remove hoverinfo: 'skip' when ready.
            // hovertemplate: formatHoverTemplate(type),
            hoverinfo: 'skip',
          },
        ]}
        layout={layout}
        config={config}
      />
    </div>
  );
};
